import type { MapId } from '~/_shared/types/map';
import { copy } from '~/_shared/utils/collections/collections';
import type { HexColor } from '~/_shared/utils/colors/colors.types';
import { type Uuid } from '~/_shared/utils/createUuid';
import { type MetricModel } from '~/_shared/utils/metric/metrics.factory';
import { type BoundaryEtag } from '~/boundary/etag/etag.types';
import { type BoundaryFillType } from '~/boundary/settings/boundaryFill.type';
import {
  type BoundaryGroupId, type BoundaryId, type BoundaryTerritoryGroupId,
} from '~/store/boundaries/boundaryIdentifier.type';
import type { ChartMetricType } from '../mapSettings/data/charts/mapSettingsCharts.types';
import {
  type BoundaryTerritoryGroupConnectedEntity,
  type BoundaryTerritoryType, type CalculateBucketFunction,
} from './boundaryTerritoryGroup.type';
import {
  type BoundaryBucketType, type BoundaryMatchingCategory, type BoundaryMatchingType, type BucketDistribution,
} from './boundaryTerritoryGroups.repository';

export type BoundaryStyle = Readonly<{
  color: HexColor;
  opacity: number;
}>;

type BoundaryTerritoryCommon = Readonly<{
  boundaryTerritoryId: string;
  displayName: string;
  translate: boolean;
  style: BoundaryStyle;
}>;

export type GeneratedBoundaryTerritory = BoundaryTerritoryCommon & Readonly<{
  custom: false;
  bucket: string;
}>;

export type CustomBoundaryTerritory = BoundaryTerritoryCommon & Readonly<{
  custom: true;
}>;

export type BoundaryTerritory = GeneratedBoundaryTerritory | CustomBoundaryTerritory;

export type BoundaryTerritoryGroupStyle = Readonly<{
  lineWidth: number;
  lineColor: HexColor;
  gradientColors: {
    minimal: {
      color: HexColor;
    };
    low: {
      color: HexColor;
    };
    medium: {
      color: HexColor;
    };
    high: {
      color: HexColor;
    };
  };
}>;

export type BoundaryTerritoryGroupDefaultStatistic = {
  type: ChartMetricType.SpreadsheetColumn;
  metric: string;
  virtualSpreadsheetId: number;
} | {
  type: ChartMetricType.Demographic;
  metric: number;
};

export type BoundaryTerritoryLabelStyle = Readonly<{
  fontColor: string;
  backgroundColor: string;
}>;

export type BoundaryTerritoryLabelValueConfig = Readonly<{
  showValue: boolean;
  includeLegend: boolean; // Toggles whether we show the column name or stat name.
  isValueCustom: boolean;
  labelValueType: BoundaryTerritoryType | null;
  labelValueColumnId: string | null;
  labelValueAggregationFunction: CalculateBucketFunction | null;
  labelValueDemographicId: string | null;
}>;

export type BoundaryTerritoryGroupSettings = Readonly<{
  allowDecimalRanges: boolean;
  boundariesAsTerritories: boolean;
  boundaryFillType: BoundaryFillType;
  boundaryTerritories: ReadonlyArray<BoundaryTerritory>;
  boundaryTerritoryType: BoundaryTerritoryType;
  bucketColumnId: string | null;
  bucketType: BoundaryBucketType | null;
  bucketDistribution: BucketDistribution;
  calculateBucketFunction: CalculateBucketFunction | null;
  connectedEntityData?: BoundaryTerritoryGroupConnectedEntityData;
  demographicId: string | null;
  displayName: null | string;
  emptyIsZero: null;
  metrics: ReadonlyArray<MetricModel>;
  rangesCount: number | null;
  style: BoundaryTerritoryGroupStyle;
  boundaryStyle: ReadonlyMap<BoundaryId, BoundaryStyle>;
  hideFillWhenInactive: boolean;
  hideFillMetrics: boolean;
  disableMapInteraction: boolean;
  hideLabels: boolean;
  hideWithoutData: boolean;
  ignoreFilters: boolean;
  selectionEditPreferredBoundaryGroup?: { id: number };
  defaultStatistics?: ReadonlyArray<BoundaryTerritoryGroupDefaultStatistic>;
  labelStyle: BoundaryTerritoryLabelStyle;
  labelValueConfig: BoundaryTerritoryLabelValueConfig;
}>;

export type BoundaryTerritoryGroupConnectedEntityData = Readonly<{
  entity: BoundaryTerritoryGroupConnectedEntity;
  id: Uuid;
}>;

export type BoundaryTerritoryGroupMatchColumn = Readonly<{
  columnId: string;
  category: BoundaryMatchingCategory;
}>;

export type BoundaryTerritoryGroupMatchings = Readonly<{
  matchingType: BoundaryMatchingType;
  matchColumns: ReadonlyArray<BoundaryTerritoryGroupMatchColumn>;
}>;

export type BoundaryTerritoryGroupModel = Readonly<{
  boundaryTerritoryGroupId?: BoundaryTerritoryGroupId;
  boundaryGroupId: BoundaryGroupId;
  mapId: MapId;
  matchings: BoundaryTerritoryGroupMatchings;
  settings: BoundaryTerritoryGroupSettings;
  archivedAt: string | null;
  etag?: BoundaryEtag;
}>;

export type BoundaryTerritoryGroup = BoundaryTerritoryGroupModel & Readonly<{
  boundaryTerritoryGroupId: number;
}>;

export type BoundaryTerritoryGroupsState = Readonly<{
  groups: ReadonlyArray<BoundaryTerritoryGroup>;
  isFetchLoading: boolean;
  isActionLoading: boolean;
  isError: boolean;
  isLoaded: boolean;
}>;

const updateBoundaryTerritories = (
  boundaryTerritoryGroups: ReadonlyArray<BoundaryTerritoryGroup>,
  boundaryTerritoryGroupId: number,
  update: (territories: ReadonlyArray<BoundaryTerritory>
  ) => ReadonlyArray<BoundaryTerritory>) => {
  return boundaryTerritoryGroups.map(group => {
    if (group.boundaryTerritoryGroupId !== boundaryTerritoryGroupId) {
      return group;
    }

    const settings = group.settings;
    const updatedBoundaryTerritories = update(settings.boundaryTerritories);
    const updatedSettings = { ...settings, boundaryTerritories: updatedBoundaryTerritories };
    return { ...group, settings: updatedSettings };
  });
};

const createCustomBoundaryTerritory = (
  id: Uuid, name: string, fillColor: HexColor, fillOpacity: number
): CustomBoundaryTerritory => ({
  boundaryTerritoryId: id,
  displayName: name,
  translate: false,
  custom: true,
  style: {
    color: fillColor,
    opacity: fillOpacity,
  },
});

export function addNewCustomBoundaryTerritory(
  state: BoundaryTerritoryGroupsState, boundaryTerritoryGroupId: number, newTerritoryId: Uuid,
  name: string, fillColor: HexColor, fillOpacity: number,
): BoundaryTerritoryGroupsState {
  const boundaryTerritory = createCustomBoundaryTerritory(newTerritoryId, name, fillColor, fillOpacity);

  const addNewCustomBoundaryTerritory = (territories: ReadonlyArray<BoundaryTerritory>) => {
    return [...territories, boundaryTerritory];
  };

  return {
    ...state,
    groups: updateBoundaryTerritories(state.groups, boundaryTerritoryGroupId, addNewCustomBoundaryTerritory),
  };
}

export function updateBoundaryTerritoryName(
  state: BoundaryTerritoryGroupsState, newName: string, territoryId: string, boundaryTerritoryGroupId: number
): BoundaryTerritoryGroupsState {
  const updateBoundaryTerritoryName = (boundaryTerritories: ReadonlyArray<BoundaryTerritory>): ReadonlyArray<BoundaryTerritory> => {
    const index = boundaryTerritories.findIndex(territory => territory.boundaryTerritoryId === territoryId);
    const boundaryTerritory = boundaryTerritories[index];
    if (!boundaryTerritory) {
      return boundaryTerritories;
    }
    const updatedBoundaryTerritory: BoundaryTerritory = { ...boundaryTerritory, displayName: newName, translate: false };

    const updatedBoundaryTerritories = copy.andReplace(boundaryTerritories, index, updatedBoundaryTerritory);

    return updatedBoundaryTerritories;
  };

  return {
    ...state, groups: updateBoundaryTerritories(state.groups, boundaryTerritoryGroupId, updateBoundaryTerritoryName),
  };
}

export function deleteBoundaryTerritory(
  state: BoundaryTerritoryGroupsState, boundaryTerritoryId: string, boundaryTerritoryGroupId: number
): BoundaryTerritoryGroupsState {
  const updatedBoundaryTerritories = updateBoundaryTerritories(state.groups, boundaryTerritoryGroupId, (boundaryTerritories) => {
    const deletedBoundaryTerritory = boundaryTerritories.find(territory => territory.boundaryTerritoryId === boundaryTerritoryId);
    if (!deletedBoundaryTerritory) {
      return boundaryTerritories;
    }

    return copy.andRemove(boundaryTerritories, [deletedBoundaryTerritory]);
  });

  return {
    ...state, groups: updatedBoundaryTerritories,
  };
}
